import React, { useState } from 'react';
import { CONTACT_INFO, COMPANY_INFO } from '../utils/constants';
import { SEO_KEYWORDS } from '../utils/seoKeywords';
import PageSEO from '../components/SEO';
import './Contact.css';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would typically send the form data to your backend
    const mailtoLink = `mailto:${CONTACT_INFO.email}?subject=Pickup Truck Rental Inquiry&body=Name: ${formData.name}%0D%0AEmail: ${formData.email}%0D%0APhone: ${formData.phone}%0D%0AService: ${formData.service}%0D%0AMessage: ${formData.message}`;
    window.open(mailtoLink);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": `Contact ${COMPANY_INFO.name}`,
    "description": "Contact us for pickup truck rental services in Dubai. Get quotes, book services, or ask questions about our 1-10 ton pickup trucks with tail lift.",
    "url": `${COMPANY_INFO.website}/contact`,
    "mainEntity": {
      "@type": "LocalBusiness",
      "name": COMPANY_INFO.name,
      "telephone": CONTACT_INFO.phone,
      "email": CONTACT_INFO.email,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": CONTACT_INFO.address,
        "addressLocality": "Dubai",
        "addressCountry": "UAE"
      },
      "openingHours": "Mo-Su 00:00-23:59",
      "priceRange": "$$"
    }
  };

  return (
    <div className="contact-page">
        <PageSEO
          title="Contact | Book Pickup Truck Rental Dubai – 0543507981"
          description="Contact Pickup Rental Truck Dubai. Quotes & bookings for 1–10 ton trucks with driver & tail lift. Same-day, 24/7. Call 0543507981 or get a quote online."
          keywords={SEO_KEYWORDS.contact}
        />
        {/* Hero Section */}
        <section className="contact-hero">
          <div className="container">
            <h1>Contact Us</h1>
            <p>Get in touch with us for reliable pickup truck rental services in Dubai</p>
          </div>
        </section>

        {/* Contact Information */}
        <section className="contact-info-section">
          <div className="container">
            <div className="contact-grid">
              <div className="contact-details">
                <h2>Get In Touch</h2>
                <p>Ready to book your pickup truck rental service? Contact us today for a quote or to discuss your requirements.</p>
                
                <div className="contact-methods">
                  <div className="contact-method">
                    <div className="method-icon">📞</div>
                    <div className="method-content">
                      <h3>Phone</h3>
                      <a href={`tel:${CONTACT_INFO.phone}`}>{CONTACT_INFO.phone}</a>
                      <p>Available 24/7 for your convenience</p>
                    </div>
                  </div>
                  
                  <div className="contact-method">
                    <div className="method-icon">✉️</div>
                    <div className="method-content">
                      <h3>Email</h3>
                      <a href={`mailto:${CONTACT_INFO.email}`}>{CONTACT_INFO.email}</a>
                      <p>Send us your requirements and get a detailed quote</p>
                    </div>
                  </div>
                  
                  <div className="contact-method">
                    <div className="method-icon">📍</div>
                    <div className="method-content">
                      <h3>Address</h3>
                      <address>{CONTACT_INFO.address}</address>
                      <p>Visit our office for in-person consultation</p>
                    </div>
                  </div>
                </div>

                <div className="quick-contact">
                  <h3>Quick Contact</h3>
                  <div className="quick-buttons">
                    <a href={`tel:${CONTACT_INFO.phone}`} className="btn-call">
                      📞 Call Now
                    </a>
                    <a href={`mailto:${CONTACT_INFO.email}`} className="btn-email">
                      ✉️ Email Us
                    </a>
                  </div>
                </div>
              </div>

              <div className="contact-form-section">
                <h2>Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your email address"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your phone number"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="service">Service Required</label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                    >
                      <option value="">Select a service</option>
                      <option value="1-ton">1 Ton Pickup Truck</option>
                      <option value="3-ton">3 Ton Pickup Truck</option>
                      <option value="5-ton">5 Ton Pickup Truck</option>
                      <option value="7-ton">7 Ton Pickup Truck</option>
                      <option value="10-ton">10 Ton Pickup Truck</option>
                      <option value="home-shifting">Home Shifting</option>
                      <option value="office-moving">Office Moving</option>
                      <option value="delivery">Delivery Services</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      placeholder="Tell us about your requirements, preferred date, pickup and delivery locations, etc."
                    />
                  </div>

                  <button type="submit" className="btn-submit">
                    Send Message
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="service-areas">
          <div className="container">
            <h2>Service Areas</h2>
            <p>We provide pickup truck rental services across all major areas of Dubai</p>
            
            <div className="areas-list">
              <div className="area-group">
                <h3>Central Dubai</h3>
                <p>Downtown Dubai, DIFC, Business Bay, Jumeirah, Marina, DSO</p>
              </div>
              <div className="area-group">
                <h3>Northern Emirates</h3>
                <p>Deira, Bur Dubai, Karama, Satwa, Al Quoz, Mirdif</p>
              </div>
              <div className="area-group">
                <h3>New Dubai</h3>
                <p>Dubai Hills, Dubai Sports City, JVC, JLT, Motor City, Arabian Ranches</p>
              </div>
              <div className="area-group">
                <h3>Industrial Areas</h3>
                <p>Al Quoz Industrial, Jebel Ali, DIP, DIC, DWC, Silicon Oasis</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faq-section">
          <div className="container">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-grid">
              <div className="faq-item">
                <h3>What are your operating hours?</h3>
                <p>We provide 24/7 pickup truck rental services across Dubai to meet your urgent transportation needs.</p>
              </div>
              <div className="faq-item">
                <h3>Do you provide drivers?</h3>
                <p>Yes, we provide professional and experienced drivers with all our truck rental services.</p>
              </div>
              <div className="faq-item">
                <h3>What is the minimum rental period?</h3>
                <p>We offer flexible rental periods starting from a few hours to several days based on your requirements.</p>
              </div>
              <div className="faq-item">
                <h3>Do your trucks have tail lift?</h3>
                <p>Yes, all our pickup trucks are equipped with tail lift for easy loading and unloading of goods.</p>
              </div>
              <div className="faq-item">
                <h3>How do I book a truck?</h3>
                <p>Simply call us at {CONTACT_INFO.phone} or email us at {CONTACT_INFO.email} with your requirements.</p>
              </div>
              <div className="faq-item">
                <h3>What areas do you serve?</h3>
                <p>We provide services across all major areas of Dubai including central, northern, and industrial zones.</p>
              </div>
            </div>
          </div>
        </section>
      </div>
  );
};

export default Contact;
